import { SharedLanguageService } from '@language';
import { FieldType } from '../../../core/field-definitions/entities/field-definition.entity';
import { SortDirection } from '../../generic-overview/components/display-settings-bar/entities/sort-direction.entity';
import { SortInfo } from '../../generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { SortOption } from '../../generic-overview/components/display-settings-bar/entities/sort-option.entity';
import { SortType } from '../../generic-overview/components/display-settings-bar/entities/sort-type.entity';
import { sortInfoToString } from '../../generic-overview/components/display-settings-bar/helpers/sort-info.helper';
import { RecordColumn } from '../entities/record-column.entity';
import { fieldSortOptionToDefaultSortOption } from './field-sort-option.helper';

export class SortOptionsHelper {
    constructor(private readonly translateService: SharedLanguageService) {}

    /**
     * Generate and combine sort-options from ListProfileFields and default sort-info
     * @param listProfileFields
     * @param defaultSortInfo
     * @returns
     */
    public getColumnSortOptions(columns: RecordColumn[], defaultSortInfo: SortInfo[]): SortOption[] {
        const sortableColumns = columns.filter(column => column.sortable);
        const fieldSortOptions = sortableColumns.flatMap(sortableColumn =>
            this.sortableColumnToSortOptions(sortableColumn)
        );

        const defaultSortOptions = this.defaultSortInfoToSortOptions(defaultSortInfo);

        return this.removeRedundantSortOptions(fieldSortOptions, defaultSortOptions);
    }

    /**
     * Translate default sort-info into sort-options
     * @param sortInfo
     * @returns
     */
    public defaultSortInfoToSortOptions(defaultSortInfo: SortInfo[]): SortOption[] {
        return defaultSortInfo.map(info => ({
            ...info,
            label: this.translateService.instant(`sort.options.${`${sortInfoToString(info)}`.toLowerCase()}`)
        }));
    }

    /**
     * Convert sortable profile-field to sort-options
     * @param sortableField
     * @returns
     */
    private sortableColumnToSortOptions(column: RecordColumn): SortOption[] {
        const ascendingOption: SortOption = {
            label: this.getSortLabel(column.translateLabel, column.sortType, SortDirection.ASC),
            field: column.key as string,
            direction: SortDirection.ASC
        };

        const descendingOption: SortOption = {
            label: this.getSortLabel(column.translateLabel, column.sortType, SortDirection.DESC),
            field: column.key as string,
            direction: SortDirection.DESC
        };

        return [ascendingOption, descendingOption];
    }

    private getSortLabel(fieldLabel: string, sortType: SortType, sortDirection: SortDirection): string {
        const directionLabelKey: string = this.translateService.translate(
            SortOptionsHelper.getSortDirectionLabelKey(sortType, sortDirection)
        );
        return `${fieldLabel} (${this.translateService.translate(directionLabelKey)})`;
    }

    public static getSortDirectionLabelKey(sortType: SortType, sortDirection: SortDirection): string {
        return `sort.type.${sortType}.${sortDirection.toLowerCase()}`;
    }

    public static getSortType(fieldType: FieldType): SortType {
        switch (fieldType) {
            case FieldType.DateField:
            case FieldType.EDTFField:
            case FieldType.TimeCodeField:
                return SortType.DATE;
            case FieldType.DoubleField:
            case FieldType.BooleanField:
            case FieldType.GeoCoordinateField:
            case FieldType.FramesField:
            case FieldType.LongField:
                return SortType.GENERIC;
            default:
                return SortType.ALPHABETICAL;
        }
    }

    /**
     * Removes options from defaultSortOptions if they are already present in the fieldSortOptions
     * @param fieldSortOptions
     * @param defaultSortOptions
     */
    private removeRedundantSortOptions(fieldSortOptions: SortOption[], defaultSortOptions: SortOption[]): SortOption[] {
        // Map Field sort options to existing default sort options
        fieldSortOptions = fieldSortOptions.map(option => fieldSortOptionToDefaultSortOption(option));

        const filteredDefaultSortOptions = defaultSortOptions.filter(
            defaultOption => !fieldSortOptions.find(fieldOption => fieldOption.field.endsWith(defaultOption.field))
        );

        return [...filteredDefaultSortOptions, ...fieldSortOptions];
    }
}
